import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  Popconfirm,
  Empty,
  Row,
  Col,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FilterOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { expenseApi, Expense, ExpenseCategory } from '../api/expenses';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const LEGAL_ENTITY_OPTIONS = [
  { value: 'autoregium', label: 'ООО АвтоРегиум' },
  { value: 'fomin', label: 'ИП Фомин Д.А.' },
  { value: 'common', label: 'Общий' },
];

const PAYMENT_TYPE_OPTIONS = [
  { value: 'cash', label: 'Наличные' },
  { value: 'bank', label: 'Банк' },
  { value: 'card', label: 'Карта' },
  { value: 'mutual', label: 'Взаимозачёт' },
  { value: 'accrual', label: 'Начисление' },
];

const LEGAL_ENTITY_COLORS: Record<string, string> = {
  autoregium: 'blue',
  fomin: 'green',
  common: 'default',
};

const PAYMENT_TYPE_LABELS: Record<string, string> = {
  cash: 'Нал',
  bank: 'Банк',
  card: 'Карта',
  mutual: 'Взчёт',
  accrual: 'Начисл',
};

interface Filters {
  search: string;
  category_id: number | undefined;
  legal_entity: string | undefined;
  date_range: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
}

const ExpensesPage: React.FC = () => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [summary, setSummary] = useState<{ total: number; by_category: { category: string; total: number }[] } | null>(null);
  const [form] = Form.useForm();
  const [filters, setFilters] = useState<Filters>({
    search: '',
    category_id: undefined,
    legal_entity: undefined,
    date_range: null,
  });

  const leafCategories = useMemo(
    () => categories.filter(c => !categories.some(ch => ch.parent_id === c.id)),
    [categories]
  );

  const buildCategoryMap = useMemo(() => {
    const map = new Map<number, ExpenseCategory>();
    categories.forEach(c => map.set(c.id, c));
    return map;
  }, [categories]);

  const getCategoryPath = (catId: number): string => {
    const parts: string[] = [];
    let curr = buildCategoryMap.get(catId);
    while (curr) {
      parts.unshift(curr.name);
      curr = curr.parent_id ? buildCategoryMap.get(curr.parent_id) : undefined;
    }
    return parts.join(' › ');
  };

  const loadCategories = useCallback(async () => {
    try {
      const data = await expenseApi.listCategories();
      setCategories(data);
    } catch {
      message.error('Ошибка загрузки категорий');
    }
  }, []);

  const loadSummary = useCallback(async () => {
    const params: Record<string, any> = {};
    if (filters.legal_entity) params.legal_entity = filters.legal_entity;
    if (filters.date_range?.[0]) params.date_from = filters.date_range[0].format('YYYY-MM-DD');
    if (filters.date_range?.[1]) params.date_to = filters.date_range[1].format('YYYY-MM-DD');
    try {
      const data = await expenseApi.summary(params);
      setSummary(data);
    } catch {
      // silently fail summary
    }
  }, [filters.legal_entity, filters.date_range]);

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    const params: Record<string, any> = {};
    if (filters.search) params.search = filters.search;
    if (filters.category_id) params.category_id = filters.category_id;
    if (filters.legal_entity) params.legal_entity = filters.legal_entity;
    if (filters.date_range?.[0]) params.date_from = filters.date_range[0].format('YYYY-MM-DD');
    if (filters.date_range?.[1]) params.date_to = filters.date_range[1].format('YYYY-MM-DD');
    try {
      const data = await expenseApi.list(params);
      setExpenses(data);
    } catch {
      message.error('Ошибка загрузки расходов');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    loadExpenses();
    loadSummary();
  }, [loadExpenses, loadSummary]);

  const handleOpenCreate = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      date: dayjs(),
      legal_entity: 'common',
      payment_type: 'bank',
    });
    setDrawerOpen(true);
  };

  const handleOpenEdit = (record: Expense) => {
    setEditingId(record.id);
    form.setFieldsValue({
      ...record,
      date: dayjs(record.date),
      category_id: record.category_id,
    });
    setDrawerOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await expenseApi.delete(id);
      message.success('Удалено');
      loadExpenses();
      loadSummary();
    } catch {
      message.error('Ошибка удаления');
    }
  };

  const handleSubmit = async (values: any) => {
    const payload = {
      ...values,
      date: values.date.format('YYYY-MM-DD'),
      amount: Number(values.amount),
    };
    try {
      if (editingId) {
        await expenseApi.update(editingId, payload);
        message.success('Обновлено');
      } else {
        await expenseApi.create(payload);
        message.success('Создано');
      }
      setDrawerOpen(false);
      loadExpenses();
      loadSummary();
    } catch {
      message.error('Ошибка сохранения');
    }
  };

  const columns: ColumnsType<Expense> = [
    {
      title: 'Дата',
      dataIndex: 'date',
      width: 110,
      render: (v: string) => dayjs(v).format('DD.MM.YYYY'),
    },
    {
      title: 'Статья',
      dataIndex: 'category_id',
      render: (_: any, record: Expense) => (
        <div>
          <Text strong>{record.category?.name || '—'}</Text>
          <div style={{ fontSize: 11, color: '#888' }}>
            {getCategoryPath(record.category_id)}
          </div>
        </div>
      ),
    },
    {
      title: 'Сумма',
      dataIndex: 'amount',
      width: 120,
      align: 'right',
      render: (v: number) => (
        <Text strong>{Number(v).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽</Text>
      ),
    },
    {
      title: 'Юрлицо',
      dataIndex: 'legal_entity',
      width: 140,
      render: (v: string) => (
        <Tag color={LEGAL_ENTITY_COLORS[v] || 'default'}>
          {LEGAL_ENTITY_OPTIONS.find(o => o.value === v)?.label || v}
        </Tag>
      ),
    },
    {
      title: 'Тип оплаты',
      dataIndex: 'payment_type',
      width: 100,
      render: (v: string) => PAYMENT_TYPE_LABELS[v] || v,
    },
    {
      title: 'Контрагент / Основание',
      render: (_: any, record: Expense) => (
        <div>
          {record.counterparty && <div><Text strong>{record.counterparty}</Text></div>}
          {record.basis && <div style={{ fontSize: 12, color: '#666' }}>{record.basis}</div>}
          {record.comment && <div style={{ fontSize: 12, color: '#888' }}>{record.comment}</div>}
        </div>
      ),
    },
    {
      title: '',
      width: 100,
      render: (_: any, record: Expense) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleOpenEdit(record)} />
          <Popconfirm
            title="Удалить расход?"
            onConfirm={() => handleDelete(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Расходы</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
          Добавить расход
        </Button>
      </div>

      {/* Сводка */}
      {summary && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col>
              <Statistic
                title="Всего расходов"
                value={summary.total}
                suffix="₽"
                precision={2}
                valueStyle={{ fontSize: 20 }}
              />
            </Col>
            <Col flex="auto">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {summary.by_category.slice(0, 8).map(item => (
                  <Tag key={item.category} color="processing">
                    {item.category}: {item.total.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ₽
                  </Tag>
                ))}
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {/* Фильтры */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap style={{ width: '100%' }}>
          <Input.Search
            placeholder="Поиск по контрагенту, основанию, комментарию"
            allowClear
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            onSearch={() => { loadExpenses(); loadSummary(); }}
            style={{ width: 300 }}
          />
          <Select
            placeholder="Категория"
            allowClear
            style={{ width: 220 }}
            value={filters.category_id}
            onChange={v => setFilters(f => ({ ...f, category_id: v }))}
            options={leafCategories.map(c => ({ value: c.id, label: getCategoryPath(c.id) }))}
          />
          <Select
            placeholder="Юрлицо"
            allowClear
            style={{ width: 180 }}
            value={filters.legal_entity}
            onChange={v => setFilters(f => ({ ...f, legal_entity: v }))}
            options={LEGAL_ENTITY_OPTIONS}
          />
          <RangePicker
            placeholder={['С даты', 'По дату']}
            value={filters.date_range as any}
            onChange={v => setFilters(f => ({ ...f, date_range: v as any }))}
          />
          <Button icon={<FilterOutlined />} onClick={() => { loadExpenses(); loadSummary(); }}>
            Применить
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => {
            setFilters({ search: '', category_id: undefined, legal_entity: undefined, date_range: null });
            setTimeout(() => { loadExpenses(); loadSummary(); }, 0);
          }}>
            Сбросить
          </Button>
        </Space>
      </Card>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={expenses}
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: <Empty description="Нет расходов" /> }}
        size="small"
      />

      {/* Drawer создания/редактирования */}
      <Drawer
        title={editingId ? 'Редактировать расход' : 'Новый расход'}
        width={480}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="date"
            label="Дата"
            rules={[{ required: true, message: 'Укажите дату' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>

          <Form.Item
            name="amount"
            label="Сумма"
            rules={[{ required: true, message: 'Укажите сумму' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={0.01}
              precision={2}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
              parser={value => (value ? value.replace(/\s/g, '') : '') as any}
              suffix="₽"
            />
          </Form.Item>

          <Form.Item
            name="category_id"
            label="Категория"
            rules={[{ required: true, message: 'Выберите категорию' }]}
          >
            <Select
              placeholder="Выберите статью расходов"
              options={leafCategories.map(c => ({ value: c.id, label: getCategoryPath(c.id) }))}
              showSearch
              filterOption={(input, option) =>
                (option?.label as string).toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            name="legal_entity"
            label="Юрлицо"
            rules={[{ required: true }]}
          >
            <Select options={LEGAL_ENTITY_OPTIONS} />
          </Form.Item>

          <Form.Item
            name="payment_type"
            label="Тип операции"
            rules={[{ required: true }]}
          >
            <Select options={PAYMENT_TYPE_OPTIONS} />
          </Form.Item>

          <Form.Item name="counterparty" label="Контрагент">
            <Input placeholder="Название контрагента" />
          </Form.Item>

          <Form.Item name="basis" label="Основание (договор, счёт, акт)">
            <Input placeholder="№ договора, счёта, акта" />
          </Form.Item>

          <Form.Item name="comment" label="Комментарий">
            <Input.TextArea rows={3} placeholder="Дополнительная информация" />
          </Form.Item>

          <Divider />

          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={() => setDrawerOpen(false)}>Отмена</Button>
            <Button type="primary" htmlType="submit">
              {editingId ? 'Сохранить' : 'Создать'}
            </Button>
          </Space>
        </Form>
      </Drawer>
    </div>
  );
};

export default ExpensesPage;
